/**
 * InfluenceGraph — Radial Influence Visualization for Profile Pages
 * ─────────────────────────────────────────────────────────────────
 * Shows how a user's projects ripple outward through the platform.
 *
 * Center: the user's project (root).
 * Ring 1: direct forks by other users (largest nodes).
 * Ring 2: forks of forks (medium nodes).
 * Ring 3+: deeper descendants (small, faded).
 *
 * Node color = zone color. Node size = fork_count of that descendant.
 * Connection lines fade with depth. Cross-zone lines glow brighter.
 *
 * Data: GET /api/users/:id/influence
 *   → { roots: [...], summary: { totalDescendants, uniqueCollaborators, maxDepth, zonesReached } }
 *
 * When a user has multiple root projects with descendants, the component
 * shows a tab bar to switch between them. Roots with no descendants are
 * grouped into a summary stat rather than empty tabs.
 *
 * Place at: src/components/profile/InfluenceGraph.jsx
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  GitFork, Users, Layers, Sparkles, ChevronLeft, ChevronRight,
  Eye, Zap, ExternalLink,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { api } from '../../api';
import { ZONES } from '../../config/zones';

// ============================================================================
// CONSTANTS
// ============================================================================

const RING_RADII    = [0, 90, 155, 205, 240]; // center, ring1, ring2, ring3, ring4
const NODE_SIZES    = [22, 14, 10, 7, 5];     // center, ring1, ring2, ring3, ring4
const LINE_OPACITY  = [0, 0.4, 0.25, 0.15, 0.1];
const NODE_OPACITY  = [1, 0.9, 0.6, 0.4, 0.3];
const SVG_SIZE      = 540; // viewBox dimensions
const CENTER        = SVG_SIZE / 2;

// ============================================================================
// LAYOUT — Compute radial positions for nodes
// ============================================================================

function computeLayout(root, descendants) {
  if (!root) return { rootNode: null, rings: [] };

  const rootNode = {
    ...root,
    x: CENTER,
    y: CENTER,
    radius: NODE_SIZES[0],
    depth: 0,
  };

  // Group descendants by depth
  const byDepth = {};
  for (const d of descendants) {
    const depth = Math.min(d.depth, 4);
    if (!byDepth[depth]) byDepth[depth] = [];
    byDepth[depth].push(d);
  }

  const rings = [];

  for (let depth = 1; depth <= 4; depth++) {
    const items = byDepth[depth] || [];
    if (items.length === 0) continue;

    const ringRadius = RING_RADII[depth];
    const nodeSize   = NODE_SIZES[depth];
    const count      = items.length;

    // Distribute evenly around the ring with a slight random offset for organic feel
    const angleStep = (2 * Math.PI) / Math.max(count, 1);
    // Start from top (–π/2) and offset slightly so ring1 and ring2 don't align
    const angleOffset = -Math.PI / 2 + (depth * 0.4);

    const ringNodes = items.map((item, i) => {
      const angle = angleOffset + i * angleStep;
      // Add subtle jitter (±8% of step) so it doesn't look machine-perfect
      const jitter = (pseudoRandom(item.id) - 0.5) * angleStep * 0.16;
      const finalAngle = angle + jitter;

      return {
        ...item,
        x: CENTER + Math.cos(finalAngle) * ringRadius,
        y: CENTER + Math.sin(finalAngle) * ringRadius,
        radius: nodeSize + Math.min(item.forkCount || 0, 6) * 0.8,
        depth,
      };
    });

    rings.push({ depth, nodes: ringNodes });
  }

  return { rootNode, rings };
}

// Deterministic pseudo-random from an integer ID (for jitter)
function pseudoRandom(id) {
  const x = Math.sin(id * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

// ============================================================================
// ZONE COLOR HELPER
// ============================================================================

function zoneColor(zoneId) {
  if (!zoneId) return '#71717a';
  const zone = ZONES[zoneId];
  return zone ? zone.color : '#71717a';
}

function zoneName(zoneId) {
  if (!zoneId) return 'Explore';
  const zone = ZONES[zoneId];
  return zone ? zone.shortLabel : 'Explore';
}

// ============================================================================
// SVG COMPONENTS
// ============================================================================

const ConnectionLine = ({ fromX, fromY, toX, toY, color, depth }) => {
  const opacity = LINE_OPACITY[Math.min(depth, 4)];
  const width = depth === 1 ? 1.5 : depth === 2 ? 1 : 0.5;
  return (
    <line
      x1={fromX} y1={fromY}
      x2={toX}   y2={toY}
      stroke={color}
      strokeWidth={width}
      opacity={opacity}
      strokeLinecap="round"
    />
  );
};

const GraphNode = ({ node, isRoot, isHovered, onHover, onLeave, onClick }) => {
  const color = zoneColor(node.zone);
  const opacity = isRoot ? 1 : NODE_OPACITY[Math.min(node.depth, 4)];
  const r = node.radius;

  return (
    <g
      className="cursor-pointer"
      onMouseEnter={() => onHover(node)}
      onMouseLeave={onLeave}
      onClick={() => onClick?.(node)}
      style={{ transition: 'transform 0.15s ease' }}
    >
      {/* Outer glow on hover */}
      {isHovered && (
        <circle
          cx={node.x} cy={node.y}
          r={r + 6}
          fill={color}
          opacity={0.12}
        />
      )}

      {/* Main circle */}
      <circle
        cx={node.x} cy={node.y}
        r={r}
        fill={color}
        opacity={isRoot ? 0.25 : opacity * 0.2}
        stroke={color}
        strokeWidth={isRoot ? 1.5 : 0.5}
      />

      {/* Inner dot */}
      <circle
        cx={node.x} cy={node.y}
        r={isRoot ? r * 0.6 : r * 0.55}
        fill={color}
        opacity={isRoot ? 0.5 : opacity * 0.4}
      />

      {/* Root project label */}
      {isRoot && (
        <text
          x={node.x}
          y={node.y + r + 14}
          textAnchor="middle"
          className="fill-current text-zinc-400"
          style={{ fontSize: '10px', fontWeight: 500 }}
        >
          {truncate(node.name, 18)}
        </text>
      )}
    </g>
  );
};

function truncate(str, max) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}

// ============================================================================
// TOOLTIP
// ============================================================================

const Tooltip = ({ node, containerRef }) => {
  if (!node) return null;

  const color = zoneColor(node.zone);
  const zone  = zoneName(node.zone);

  // Position tooltip near the node but keep it inside the container
  let tx = node.x;
  let ty = node.y - node.radius - 12;
  const anchor = ty < 60 ? 'top' : 'bottom';
  if (anchor === 'top') ty = node.y + node.radius + 12;

  return (
    <foreignObject
      x={tx - 90} y={anchor === 'bottom' ? ty - 52 : ty}
      width={180} height={56}
      style={{ pointerEvents: 'none', overflow: 'visible' }}
    >
      <div
        className="bg-zinc-900/95 backdrop-blur border border-zinc-700/60 rounded-lg px-3 py-2 shadow-xl"
        style={{ fontSize: '11px' }}
      >
        <p className="text-white font-medium truncate leading-tight">
          {node.name || 'Untitled'}
        </p>
        <div className="flex items-center gap-2 mt-1 text-zinc-500">
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: color }}
          />
          <span>{zone}</span>
          {node.author && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="truncate">@{node.author.username}</span>
            </>
          )}
          {node.forkCount > 0 && (
            <>
              <span className="text-zinc-700">·</span>
              <GitFork className="w-2.5 h-2.5 inline" />
              <span>{node.forkCount}</span>
            </>
          )}
        </div>
      </div>
    </foreignObject>
  );
};

// ============================================================================
// RING LABELS (concentric ring guides)
// ============================================================================

const RingGuides = ({ maxDepth }) => (
  <g opacity={0.06}>
    {[1, 2, 3, 4].filter(d => d <= Math.max(maxDepth, 1)).map(depth => (
      <circle
        key={depth}
        cx={CENTER} cy={CENTER}
        r={RING_RADII[depth]}
        fill="none"
        stroke="currentColor"
        strokeWidth={0.5}
        strokeDasharray="3 6"
        className="text-zinc-400"
      />
    ))}
  </g>
);

// ============================================================================
// ROOT SELECTOR (tab bar for switching between root projects)
// ============================================================================

const RootSelector = ({ roots, activeIndex, onChange }) => {
  if (roots.length <= 1) return null;

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
      {roots.map((root, i) => {
        const color = zoneColor(root.zone);
        const active = i === activeIndex;
        const count = root.descendants?.length || 0;

        return (
          <button
            key={root.id}
            onClick={() => onChange(i)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all',
              'border',
              active
                ? 'border-zinc-600 bg-zinc-800/80 text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/40',
            )}
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: color, opacity: active ? 1 : 0.5 }}
            />
            <span className="truncate max-w-[100px]">{root.name}</span>
            {count > 0 && (
              <span className="text-zinc-600 tabular-nums">{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// SUMMARY STATS
// ============================================================================

const SummaryBar = ({ summary }) => {
  const stats = [
    { icon: GitFork,  label: 'Forks out',     value: summary.totalDescendants },
    { icon: Users,    label: 'Collaborators',  value: summary.uniqueCollaborators },
    { icon: Layers,   label: 'Deepest chain',  value: summary.maxDepth },
    { icon: Sparkles, label: 'Zones reached',  value: summary.zonesReached },
  ].filter(s => s.value > 0);

  if (stats.length === 0) return null;

  return (
    <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
      {stats.map(({ icon: Icon, label, value }) => (
        <div key={label} className="flex items-center gap-1.5 text-xs text-zinc-500">
          <Icon className="w-3 h-3 text-zinc-600" />
          <span className="tabular-nums text-zinc-300 font-medium">{value}</span>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// EMPTY STATE
// ============================================================================

const EmptyState = ({ isOwnProfile }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <div className="w-12 h-12 rounded-xl bg-zinc-800/60 flex items-center justify-center mb-3">
      <GitFork className="w-5 h-5 text-zinc-600" />
    </div>
    <p className="text-sm text-zinc-500 max-w-[240px] leading-relaxed">
      {isOwnProfile
        ? 'When others fork your projects, your influence graph will grow here.'
        : 'No forks yet — this user\'s creative influence is just getting started.'
      }
    </p>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const InfluenceGraph = ({ userId, isOwnProfile = false, className }) => {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeRoot, setActiveRoot] = useState(0);
  const [hoveredNode, setHoveredNode] = useState(null);
  const svgRef = useRef(null);

  // ── Fetch influence data ──────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    api.get(`/users/${userId}/influence`)
      .then(res => {
        if (!cancelled) {
          setData(res.data);
          setActiveRoot(0);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Failed to load influence graph:', err);
          setError('Could not load influence data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userId]);

  // ── Compute layout for the active root ────────────────────────────
  const { rootNode, rings, allNodes, parentMap } = useMemo(() => {
    if (!data?.roots?.length) {
      return { rootNode: null, rings: [], allNodes: [], parentMap: new Map() };
    }

    const root = data.roots[Math.min(activeRoot, data.roots.length - 1)];
    const descendants = root.descendants || [];
    const layout = computeLayout(root, descendants);

    // Build a flat list for connections and a parent→child map
    const flat = [];
    const pMap = new Map();
    if (layout.rootNode) flat.push(layout.rootNode);

    for (const ring of layout.rings) {
      for (const node of ring.nodes) {
        flat.push(node);
        // Map child → parent coordinates (for connection lines)
        const parentId = node.parentId;
        // Find the parent in flat list or use root
        const parentNode = flat.find(n => n.id === parentId) || layout.rootNode;
        if (parentNode) pMap.set(node.id, parentNode);
      }
    }

    return {
      rootNode: layout.rootNode,
      rings: layout.rings,
      allNodes: flat,
      parentMap: pMap,
    };
  }, [data, activeRoot]);

  // ── Roots with descendants (for tabs) ─────────────────────────────
  const influentialRoots = useMemo(() => {
    if (!data?.roots) return [];
    return data.roots.filter(r => r.descendants && r.descendants.length > 0);
  }, [data]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleNodeClick = useCallback((node) => {
    if (node.depth === 0) return; // clicking root does nothing special
    // Open project in new context — navigate or deep link
    const projectId = node.id;
    window.open(`/dashboard?project=${projectId}`, '_self');
  }, []);

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={cn('mt-6', className)}>
        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-4 h-4 rounded bg-zinc-800 animate-pulse" />
            <div className="h-3 w-28 rounded bg-zinc-800 animate-pulse" />
          </div>
          <div className="flex justify-center py-12">
            <div className="w-32 h-32 rounded-full border border-zinc-800/50 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) return null;

  // Nothing to show
  const hasInfluence = data.summary.totalDescendants > 0;
  if (!hasInfluence) {
    return (
      <div className={cn('mt-6', className)}>
        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl overflow-hidden">
          <div className="px-5 pt-4 pb-2">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-purple-400" />
              Influence
            </h3>
          </div>
          <EmptyState isOwnProfile={isOwnProfile} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('mt-6', className)}>
      <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl overflow-hidden">

        {/* Header */}
        <div className="px-5 pt-4 pb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-purple-400" />
            Influence
          </h3>
        </div>

        {/* Root selector tabs */}
        {influentialRoots.length > 1 && (
          <div className="px-5 pb-2">
            <RootSelector
              roots={influentialRoots}
              activeIndex={activeRoot}
              onChange={setActiveRoot}
            />
          </div>
        )}

        {/* SVG radial graph */}
        <div className="px-2 pb-2" ref={svgRef}>
          <svg
            width="100%"
            viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
            className="max-h-[420px]"
          >
            {/* Ring guides */}
            <RingGuides maxDepth={data.summary.maxDepth} />

            {/* Connection lines (rendered first, behind nodes) */}
            {rings.map(ring =>
              ring.nodes.map(node => {
                const parent = parentMap.get(node.id);
                if (!parent) return null;
                const color = zoneColor(node.zone);
                return (
                  <ConnectionLine
                    key={`line-${node.id}`}
                    fromX={parent.x} fromY={parent.y}
                    toX={node.x}     toY={node.y}
                    color={color}
                    depth={node.depth}
                  />
                );
              })
            )}

            {/* Descendant nodes (ring by ring, inner first) */}
            {rings.map(ring =>
              ring.nodes.map(node => (
                <GraphNode
                  key={`node-${node.id}`}
                  node={node}
                  isRoot={false}
                  isHovered={hoveredNode?.id === node.id}
                  onHover={setHoveredNode}
                  onLeave={() => setHoveredNode(null)}
                  onClick={handleNodeClick}
                />
              ))
            )}

            {/* Root node (on top of everything) */}
            {rootNode && (
              <GraphNode
                node={rootNode}
                isRoot
                isHovered={hoveredNode?.id === rootNode.id}
                onHover={setHoveredNode}
                onLeave={() => setHoveredNode(null)}
              />
            )}

            {/* Tooltip (on top of all nodes) */}
            {hoveredNode && hoveredNode.depth > 0 && (
              <Tooltip node={hoveredNode} containerRef={svgRef} />
            )}
          </svg>
        </div>

        {/* Summary stats */}
        <div className="px-5 pb-4">
          <SummaryBar summary={data.summary} />
        </div>
      </div>
    </div>
  );
};

export default InfluenceGraph;